import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Tabs, Tab, useTheme, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CompactProjectionsHeader from './components/CompactProjectionsHeader';
import SimpleProjectionsVisualization from './components/SimpleProjectionsVisualization';
import ProjectionChartsGrid from './components/charts/ProjectionChartsGrid';
import ProjectionsSidebar from './components/ProjectionsSidebar';
import ProjectionsHelpModal from './components/ProjectionsHelpModal';
import { AnalystEstimatesTable } from '../../shared/AnalystEstimatesTable';
import { Quote } from '../Charts/hooks/useCompanyQuote';
import { CompanyProfile } from '../Charts/hooks/useCompanyProfile';
import { useCompanySimpleProjectionAssumptions } from '../Charts/hooks/useCompanySimpleProjectionAssumptions';
import { calculateSimpleProjections } from './utils/calculateSimpleProjections';
import { GrowthOverrides } from '../../../types/SimpleProjection';
import { isPenceCurrency } from '../../../utils/stockPriceFormatter';
import { hapticSelection } from '../../../lib/haptics';

interface ProjectionsWrapperProps {
  ticker: string;
  quote: Quote;
  profile?: CompanyProfile;
}

const ProjectionsWrapper: React.FC<ProjectionsWrapperProps> = ({ ticker, quote, profile }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [, setSearchParams] = useSearchParams();

  // Use company name from quote prop
  const companyName = quote.name ?? '';

  // Tab state (0 = Growth Projections, 1 = Analyst Estimates)
  const [tabValue, setTabValue] = useState<number>(0);

  // Help modal state
  const [helpModalOpen, setHelpModalOpen] = useState<boolean>(false);

  // Growth Projections view: data table vs charts (parameters drive both)
  const [projectionView, setProjectionView] = useState<'table' | 'charts'>('table');

  // Simple projection parameters (PE ratio is user-controllable)
  const [simpleRevenueGrowth, setSimpleRevenueGrowth] = useState<number>(0);
  const [netIncomeMargin, setNetIncomeMargin] = useState<number>(0);
  const [peRatio, setPeRatio] = useState<number>(15);
  const [dilutionRate, setDilutionRate] = useState<number>(0);

  // Per-year overrides for manual cell edits (growth rates, not absolute
  // values). These persist when sliders change: a slider sets the baseline
  // for any year the user hasn't manually edited, so moving Dilution, PE,
  // etc. re-flows the non-edited years without discarding the user's edits.
  // The Reset button restores everything to the loaded baseline.
  const [growthOverrides, setGrowthOverrides] = useState<GrowthOverrides>({});

  // Per-year estimates are company-specific — clear them when the ticker
  // changes so one company's edits don't carry over to another.
  useEffect(() => {
    setGrowthOverrides({});
  }, [ticker]);

  // Handle cell edit from the visualization component
  const handleCellEdit = useCallback(
    (yearLabel: string, field: 'revenueGrowthPct' | 'netIncomeMarginPct' | 'peRatioLow' | 'peRatioHigh', value: number | undefined) => {
      setGrowthOverrides((prev) => {
        const newOverrides = { ...prev };

        // If value is undefined, remove the override for this field
        if (value === undefined) {
          if (newOverrides[yearLabel]) {
            delete newOverrides[yearLabel][field];
            // Clean up empty year entries
            if (Object.keys(newOverrides[yearLabel]).length === 0) {
              delete newOverrides[yearLabel];
            }
          }
        } else {
          // Set or update the override
          newOverrides[yearLabel] = {
            ...newOverrides[yearLabel],
            [field]: value,
          };
        }

        return newOverrides;
      });
    },
    []
  );

  // Simple projection data
  const {
    data: simpleAssumptions,
    isLoading: isLoadingSimpleAssumptions,
    error: simpleAssumptionsError,
  } = useCompanySimpleProjectionAssumptions(ticker);

  // Reset to the calculated baseline: slider assumptions restored to their
  // loaded values and all manual cell edits cleared.
  const handleReset = useCallback(() => {
    if (simpleAssumptions) {
      setSimpleRevenueGrowth(simpleAssumptions.revenueGrowthSmoothed);
      setNetIncomeMargin(simpleAssumptions.netIncomeMargin);
      setPeRatio(simpleAssumptions.peRatio);
      setDilutionRate(simpleAssumptions.sharesGrowthSmoothed);
    }
    setGrowthOverrides({});
  }, [simpleAssumptions]);

  // Calculate simple projections client-side
  // Note: PE ratio is user-controllable via slider
  const simpleProjections = React.useMemo(() => {
    if (!simpleAssumptions) return null;

    return calculateSimpleProjections({
      assumptions: simpleAssumptions,
      revenueGrowthPct: simpleRevenueGrowth,
      netIncomeMarginPct: netIncomeMargin,
      peRatio: peRatio,
      dilutionRate,
      numberOfYears: 5,
      growthOverrides,
    });
  }, [simpleAssumptions, simpleRevenueGrowth, netIncomeMargin, peRatio, dilutionRate, growthOverrides]);

  // Handle back button
  const handleBack = useCallback(() => {
    hapticSelection();
    setSearchParams({});
  }, [setSearchParams]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    hapticSelection();
    setTabValue(newValue);
  };

  // Auto-populate simple projection parameters when assumptions load
  // Note: PE ratio is user-controllable and initialized from actual data
  React.useEffect(() => {
    if (simpleAssumptions) {
      setSimpleRevenueGrowth(simpleAssumptions.revenueGrowthSmoothed);
      setNetIncomeMargin(simpleAssumptions.netIncomeMargin);
      setPeRatio(simpleAssumptions.peRatio);
      setDilutionRate(simpleAssumptions.sharesGrowthSmoothed);
    }
  }, [simpleAssumptions]);

  // Calculate fair value and upside for Growth Projections
  // We use the high estimate share price as the "predicted" value
  const currentPrice = quote.price;

  const fairValue = simpleProjections?.[simpleProjections.length - 1]?.sharePriceHigh;

  // For GBX stocks, normalize the quote price (pence) to pounds for comparison
  // because calculated projections (EPS × PE) are in pounds (matching financial statements)
  const normalizedCurrentPrice = isPenceCurrency(profile?.currency)
    ? (currentPrice ?? 0) / 100
    : currentPrice;

  const upside =
    normalizedCurrentPrice && fairValue
      ? ((fairValue - normalizedCurrentPrice) / normalizedCurrentPrice) * 100
      : null;

  // Extract exchange from profile prop
  const exchange = profile?.exchange;

  // Table / Charts view toggle — rendered in the header on desktop (next to
  // Help) so the page is left clear for the sidebar + table; rendered above
  // the table on mobile, where the header is too tight.
  const viewToggle = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {(['table', 'charts'] as const).map((view) => {
        const active = projectionView === view;
        const Icon = view === 'table' ? TableChartOutlinedIcon : BarChartOutlinedIcon;
        const label = view === 'table' ? 'Table' : 'Charts';
        return (
          <Button
            key={view}
            onClick={() => {
              hapticSelection();
              setProjectionView(view);
            }}
            disableRipple
            startIcon={<Icon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
              color: active ? '#22c55e' : 'text.secondary',
              minHeight: 32,
              py: 0.5,
              px: 2,
              borderRadius: 0.5,
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#212121' : '#f5f5f5'),
              border: (theme) =>
                active
                  ? '1px solid #22c55e'
                  : theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
              '&:hover': {
                color: active ? '#22c55e' : 'text.primary',
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#212121' : '#e5e5e5'),
              },
            }}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
      }}
    >
      {/* Compact Header - Sticky at top */}
      <CompactProjectionsHeader
        ticker={ticker}
        companyName={companyName}
        exchange={exchange}
        currentPrice={currentPrice}
        fairValue={fairValue}
        upside={upside}
        onBack={handleBack}
        fairValueLabel="Predicted Share Price"
        onHelpClick={tabValue === 0 ? () => setHelpModalOpen(true) : undefined}
      />

      {/* Tab Navigation - Below header. Tabs at flex-start, view toggle at flex-end (desktop) */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          paddingTop: 2,
          backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            minHeight: 32,
            '& .MuiTabs-indicator': {
              display: 'none', // Hide default indicator
            },
            '& .MuiTabs-flexContainer': {
              gap: 1,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'text.secondary',
              py: 0,
              px: 2,
              minHeight: 32,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? '#212121' : '#f5f5f5',
              border: (theme) =>
                theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: 0.5,
              transition: 'all 200ms linear',
              '&.Mui-selected': {
                color: '#22c55e',
                fontWeight: 600,
                borderColor: '#22c55e',
              },
              '&:hover': {
                color: 'text.primary',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? '#212121' : '#e5e5e5',
              },
            },
          }}
        >
          <Tab label="Growth Projections" />
          <Tab label="Analyst Estimates" />
        </Tabs>

        {/* View toggle - desktop only (mobile shows it above the table) */}
        {!isMobile && tabValue === 0 && viewToggle}
      </Box>

      {/* Content Area - full width with consistent gutters */}
      <Box sx={{ px: { xs: 2, md: 3 }, pb: 4 }}>
        {tabValue === 0 && (
          /* Growth Projections Content */
          <Box
            data-tour="projections-controls"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              // Stretch on mobile (column) so the table/charts fill the width;
              // flex-start on desktop (row) so the sticky sidebar doesn't stretch.
              alignItems: { xs: 'stretch', md: 'flex-start' },
              gap: 2,
              pt: { xs: 2, md: 3 },
            }}
          >
            {/* Parameters Sidebar - sticky left rail on desktop, stacked on mobile */}
            <ProjectionsSidebar
              revenueGrowth={simpleRevenueGrowth}
              setRevenueGrowth={setSimpleRevenueGrowth}
              netIncomeMargin={netIncomeMargin}
              setNetIncomeMargin={setNetIncomeMargin}
              peRatio={peRatio}
              setPeRatio={setPeRatio}
              dilutionRate={dilutionRate}
              setDilutionRate={setDilutionRate}
              assumptions={simpleAssumptions}
              onReset={handleReset}
              isMobile={isMobile}
            />

            {/* Main content - fills remaining width */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Mobile keeps the toggle above the table; desktop shows it in the header */}
              {isMobile && <Box sx={{ mb: 2 }}>{viewToggle}</Box>}

              {projectionView === 'table' && (
                <SimpleProjectionsVisualization
                  projections={simpleProjections}
                  assumptions={simpleAssumptions}
                  isLoading={isLoadingSimpleAssumptions}
                  error={simpleAssumptionsError}
                  onCellEdit={handleCellEdit}
                  isMobile={isMobile}
                />
              )}

              {projectionView === 'charts' && simpleProjections && simpleProjections.length > 0 && (
                <ProjectionChartsGrid projections={simpleProjections} ticker={ticker} />
              )}
            </Box>
          </Box>
        )}

        {tabValue === 1 && (
          /* Analyst Estimates Content */
          <Box sx={{ pt: 3 }}>
            <AnalystEstimatesTable ticker={ticker} />
          </Box>
        )}
      </Box>

      {/* Help Modal */}
      <ProjectionsHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
    </Box>
  );
};

export default ProjectionsWrapper;
